/**
 * Lightweight SFX helper built on Web Audio API.
 *
 * No audio files needed — tones are synthesised on the fly, keeping the bundle
 * small and avoiding CORS / preload issues. Every call is a no-op when the
 * browser blocks audio (before first user interaction) or the API isn't
 * available (SSR / very old browsers).
 *
 * Public API:
 *   playSuccess()  — cheerful two-note chime for a successful delivery
 *   playError()    — low buzz for an undelivered / cancel action
 *   playClick()    — soft high-pitched tick for button presses
 *   playSettle()   — celebratory arpeggio for finishing an assignment
 */

let ctx = null;
let muted = false;

// Persist user's mute preference in localStorage. `sfxEnabled` returns true by
// default; the driver can call `setSfxMuted(true)` to silence.
try {
  muted = localStorage.getItem("sfx.muted") === "1";
} catch {
  muted = false;
}

export function setSfxMuted(next) {
  muted = !!next;
  try {
    localStorage.setItem("sfx.muted", muted ? "1" : "0");
  } catch { /* ignore quota errors */ }
}

export function isSfxMuted() {
  return muted;
}

function getCtx() {
  if (muted) return null;
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!ctx) ctx = new Ctx();
  // Resume if suspended (autoplay policy — first user gesture).
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

/**
 * Play a short envelope-shaped tone at the given frequency.
 * @param {number} freq       — pitch in Hz
 * @param {number} duration   — seconds
 * @param {string} type       — oscillator type (sine/triangle/square/sawtooth)
 * @param {number} gainPeak   — peak volume 0..1
 * @param {number} startAt    — offset in seconds
 */
function tone(freq, duration = 0.12, type = "sine", gainPeak = 0.25, startAt = 0) {
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime + startAt;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(gainPeak, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

export function playSuccess() {
  // 2-note ascending chime C5 → G5.
  tone(523.25, 0.10, "triangle", 0.22, 0);
  tone(783.99, 0.14, "triangle", 0.20, 0.09);
}

export function playError() {
  // Low descending buzz.
  tone(196, 0.20, "sawtooth", 0.14, 0);
  tone(147, 0.20, "sawtooth", 0.12, 0.10);
}

export function playClick() {
  tone(1200, 0.04, "triangle", 0.12, 0);
}

export function playSettle() {
  // Bright celebratory arpeggio C5-E5-G5-C6.
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((n, i) => tone(n, 0.12, "triangle", 0.22, i * 0.08));
}

/**
 * Notification chime — 3-tone attention-grabbing ping (E5 → G5 → C6).
 * Slightly longer than click, softer than settle. Used for incoming admin
 * broadcasts so drivers notice them even from another tab.
 */
export function playNotification() {
  tone(659.25, 0.13, "triangle", 0.24, 0);
  tone(783.99, 0.13, "triangle", 0.22, 0.10);
  tone(1046.5, 0.17, "triangle", 0.20, 0.20);
}
